import React, { useState, useEffect } from 'react';
import { Phone, AlertTriangle } from 'lucide-react';
import supabase from '../lib/supabaseClient';
import { VISIBLE_TENANTS_OR } from '../lib/tenantVisibility';

export default function VoiceTrunkProvisioning() {
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from('tenants')
        .select('id, name, twilio_subaccount_sid, twilio_voice_trunk_sid')
        .or(VISIBLE_TENANTS_OR)
        .order('name');
      if (!error && data) setTenants(data);
    })();
  }, []);

  const selectedTenant = tenants.find(t => t.id === selectedTenantId);
  const canSend = !!selectedTenantId && !selectedTenant?.twilio_subaccount_sid;

  const handleProvision = async () => {
    setSending(true);
    setResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch('/api/admin/a2p/provision-voice-trunk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ tenant_id: selectedTenantId }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setResult({ ok: false, text: data.error || 'Something went wrong.' });
      } else {
        setResult({ ok: true, text: `Done — subaccount ${data.subaccount_sid}, trunk ${data.trunk_sid}. "Set Up Voice AI" will now work for this tenant.` });
        setTenants(prev => prev.map(t => t.id === selectedTenantId
          ? { ...t, twilio_subaccount_sid: data.subaccount_sid, twilio_voice_trunk_sid: data.trunk_sid }
          : t));
      }
    } catch (err) {
      setResult({ ok: false, text: err.message || 'Something went wrong.' });
    }
    setSending(false);
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="flex items-center gap-2 mb-1">
        <Phone className="w-6 h-6 text-indigo-500" />
        <h1 className="text-xl font-bold text-gray-900">Voice Trunk Provisioning</h1>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Creates a Twilio subaccount + voice SIP trunk for a tenant that has neither — normally a
        side effect of real A2P registration, but a test tenant on the A2P bypass list (SurFox AI
        Demo, Baramundi) skips that, leaving "Set Up Voice AI" unable to purchase a number. Use
        this to unblock those tenants without a terminal.
      </p>

      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-5">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-2">Tenant</label>
          <select
            value={selectedTenantId}
            onChange={(e) => { setSelectedTenantId(e.target.value); setResult(null); }}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">Select a tenant…</option>
            {tenants.map(t => (
              <option key={t.id} value={t.id}>
                {t.name}{t.twilio_subaccount_sid ? (t.twilio_voice_trunk_sid ? ' — already has a trunk' : ' — has subaccount, no trunk') : ''}
              </option>
            ))}
          </select>
          {selectedTenant?.twilio_subaccount_sid && (
            <div className="flex items-start gap-2 mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>
                {selectedTenant.twilio_voice_trunk_sid
                  ? 'This tenant already has a subaccount and trunk — nothing to do.'
                  : 'This tenant already has a subaccount but no trunk. This tool only handles "neither exists" — use scripts/backfill-voice-trunks.js for this case instead.'}
              </span>
            </div>
          )}
        </div>

        <button
          onClick={handleProvision}
          disabled={!canSend || sending}
          className="w-full py-2.5 rounded-lg font-semibold text-white bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {sending ? 'Provisioning…' : 'Provision Subaccount + Trunk'}
        </button>

        {result && (
          <p className={`text-sm ${result.ok ? 'text-green-600' : 'text-red-600'}`}>{result.text}</p>
        )}
      </div>
    </div>
  );
}
